/* eslint-disable @typescript-eslint/no-explicit-any */
import { BigNumber } from '@ethersproject/bignumber';
import {
  useVenoFountainData,
  useVenoReservoirData,
} from '@ui/components/VenoInfoView/useVenoInfoData';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { coinModes } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { usePendingVnoAllGardens } from '@ui/hooks/usePendingVno';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import type { GardenType } from '@ui/utils/garden';
import { gardenTypes } from '@ui/utils/garden';
import { resetWhenRouterChange } from '@ui/utils/zustandMiddleware';
import type { VaultType } from '@ui/views/Vno/ReservoirAndFountain/types';
import type { EthersError } from '@veno-app/sdk';
import { useMemo } from 'react';
import { create } from 'zustand';

export enum HarvestAllStep {
  LIST,
  HARVEST_GARDEN,
}

export type HarvestElement = GardenType | VaultType;
export type GardenWithCoinMode = [CoinMode, GardenType];

export type ReceivedAmounts = {
  vno?: BigNumber;
  cro?: BigNumber;
  atom?: BigNumber;
  vnoInFountain?: BigNumber;
  vnoInReservoir?: BigNumber;
};

export type UseHarvestAllDrawerStore = {
  isOpen: boolean;
  isTransactionPending: boolean;
  step: HarvestAllStep;
  onOpen: () => void;
  onClose: () => void;
  defaultLockVault: VaultType;
  harvestableVaults: VaultType[];
  harvestableGardens: GardenWithCoinMode[];
  activeGarden?: GardenWithCoinMode;
  harvestedVaults: VaultType[];
  harvestedGardens: GardenWithCoinMode[];
  error: EthersError;
  addHarvestedGarden: (garden: GardenWithCoinMode) => void;
  addHarvestedVault: (vault: VaultType) => void;
  harvestedReservoirRewards?: { wcro: BigNumber; atom: BigNumber };
  updateHarvestedReservoirRewards: (wcro: BigNumber, atom: BigNumber) => void;
  updateHarvestedFountainRewards: (rewards: BigNumber) => void;
  harvestedFountainRewards?: BigNumber;
  harvestedGardenRewards: Record<
    CoinMode,
    Record<
      GardenType,
      {
        data: BigNumber | undefined;
        usd?: BigNumber | undefined;
      }
    >
  >;
  addHarvestedGardenRewards: (
    garden: GardenWithCoinMode,
    rewards: BigNumber,
    usdRewards: BigNumber,
  ) => void;
};

const harvestedGardenRewardsInitial: Record<
  CoinMode,
  Record<
    GardenType,
    {
      data: BigNumber | undefined;
      usd?: BigNumber | undefined;
    }
  >
  // it's not readable if we don't use "any"
> = Object.values(coinModes).reduce((acc: any, coinModeKey: CoinMode) => {
  acc[coinModeKey] = Object.values(gardenTypes).reduce(
    (gardenAcc: any, gardenTypeKey: GardenType) => {
      gardenAcc[gardenTypeKey] = { data: undefined, usd: undefined };
      return gardenAcc;
    },
    {},
  );
  return acc;
}, {});

export const useHarvestAllDrawer = create(
  resetWhenRouterChange<UseHarvestAllDrawerStore>((set, get) => ({
    isOpen: false,
    isTransactionPending: false,
    step: HarvestAllStep.LIST,
    error: { code: '', message: '' },
    harvestableVaults: [],
    harvestableGardens: [],
    harvestedGardens: [],
    defaultLockVault: 'FOUNTAIN',
    harvestedVaults: [],
    harvestedGardenRewards: harvestedGardenRewardsInitial,
    updateHarvestedReservoirRewards: (wcro, atom) => {
      const oldRewards = get().harvestedReservoirRewards;
      set({
        harvestedReservoirRewards: {
          atom: atom.add(oldRewards?.atom ?? BigNumber.from(0)),
          wcro: wcro.add(oldRewards?.wcro ?? BigNumber.from(0)),
        },
      });
    },
    updateHarvestedFountainRewards: (rewards) => {
      const oldRewards = get().harvestedFountainRewards;
      set({
        harvestedFountainRewards: rewards.add(oldRewards ?? BigNumber.from(0)),
      });
    },
    addHarvestedGarden: (garden) => {
      set({ harvestedGardens: [garden, ...get().harvestedGardens] });
    },
    addHarvestedVault: (vault) => {
      set({ harvestedVaults: [vault, ...get().harvestedVaults] });
    },
    onOpen: () => {
      set({
        isOpen: true,
      });
    },
    addHarvestedGardenRewards: (
      garden: GardenWithCoinMode,
      rewards: BigNumber,
      usdRewards: BigNumber,
    ) => {
      const harvestedGardenRewards = get().harvestedGardenRewards;
      harvestedGardenRewards[garden[0]][garden[1]].data = rewards;
      harvestedGardenRewards[garden[0]][garden[1]].usd = usdRewards;
      set({ harvestedGardenRewards });
    },
    onClose: () => {
      useHarvestAllDrawer.getState().$$resetData?.();
    },
  })),
);

export const useHarvestAllRewardsInfo = () => {
  const { pendingRewards: pendingRewardsFountain } = useVenoFountainData();
  const { pendingRewardsMap: pendingRewardsReservoir } = useVenoReservoirData();
  const { data: gardensRetMap, usdData: gardensUsdValueMap } =
    usePendingVnoAllGardens();

  const pendingRewardsMap: Record<
    CoinMode,
    Record<GardenType, { data: BigNumber | undefined; usd?: BigNumber }>
  > = useMemo(
    () =>
      coinModes.reduce(
        (acc, mode) => ({
          ...acc,
          [mode]: {
            TECTONIC: {
              data: gardensRetMap[mode]['TECTONIC'].data,
              usd: gardensUsdValueMap[mode]['TECTONIC'],
            },
            FERRO: {
              data: gardensRetMap[mode]['FERRO'].data,
              usd: gardensUsdValueMap[mode]['FERRO'],
            },
            VVS: {
              data: gardensRetMap[mode]['VVS'].data,
              usd: gardensUsdValueMap[mode]['VVS'],
            },
            VVS_CRO: {
              data: gardensRetMap[mode]['VVS_CRO'].data,
              usd: gardensUsdValueMap[mode]['VVS_CRO'],
            },
          },
        }),
        {} as Record<
          CoinMode,
          Record<GardenType, { data: BigNumber | undefined; usd?: BigNumber }>
        >,
      ),
    [gardensRetMap, gardensUsdValueMap],
  );

  const gardensHarvestable: Array<GardenWithCoinMode> = useMemo(() => {
    const arr: Array<GardenWithCoinMode> = [];

    Object.entries(pendingRewardsMap).forEach(
      ([coinMode, harvestElementMap]) => {
        Object.entries(harvestElementMap).forEach(([harvestElement, value]) => {
          if (value.usd && value.usd.gt(BigNumber.from(10).pow(18).mul(2))) {
            arr.push([coinMode as CoinMode, harvestElement as GardenType]);
          }
        });
      },
    );

    return arr;
  }, [pendingRewardsMap]);

  const pendingRewardsReservoirUsd = (
    useCoinUsdValue(pendingRewardsReservoir.wcro, 'cro').data ??
    BigNumber.from(0)
  ).add(
    useCoinUsdValue(pendingRewardsReservoir.wcro, 'cro')?.data ??
      BigNumber.from(0),
  );

  const pendingRewardsFountainUsd = useVnoUsdValue(pendingRewardsFountain).data;

  const reservoirHarvestable = pendingRewardsReservoirUsd.gt(
    BigNumber.from(10).pow(18).mul(2),
  );
  const fountainHarvestable = (
    pendingRewardsFountainUsd ?? BigNumber.from(0)
  ).gt(BigNumber.from(10).pow(18).mul(2));

  return {
    pendingRewardsMap,
    gardensHarvestable,
    fountainHarvestable,
    reservoirHarvestable,
    pendingRewardsReservoir,
    pendingRewardsFountain,
    pendingRewardsReservoirUsd,
    pendingRewardsFountainUsd,
  };
};
